import type { ReactNode, CSSProperties } from 'react'
import { useRef, useLayoutEffect } from 'react'
import { isEqual } from 'lodash'
import 'src/styles/components/common/nativeTable.less'

export type ColumnItem = {
  title: string,
  dataIndex: string | number,
  width?: number,
  fixed?: 'left' | 'right',
  render?: (record: any, index: number) => ReactNode
}

const NativeTable = (props: {
  columns: ColumnItem[],
  dataSource: any[]
}) => {
  const fixedStatus = useRef({ left: false, right: false })
  const handleTableScroll = (e: any) => {
    const { scrollLeft, scrollWidth, offsetWidth } = e.nativeEvent.target
    const newStatus = {
      left: !!scrollLeft,
      right: !(scrollLeft + offsetWidth >= scrollWidth)
    }
    if (!isEqual(fixedStatus.current, newStatus)) {
      Array.from(e.nativeEvent.target.getElementsByClassName('fixed left')).forEach((ele: any) => {
        ele.classList[newStatus.left ? 'add' : 'remove']('fixed-left')
      })
      Array.from(e.nativeEvent.target.getElementsByClassName('fixed right')).forEach((ele: any) => {
        ele.classList[newStatus.right ? 'add' : 'remove']('fixed-right')
      })
      fixedStatus.current = newStatus
    }
  }

  useLayoutEffect(() => {
    handleTableScroll({ nativeEvent: { target: document.getElementsByClassName('native-table')[0] } })
  }, [props.dataSource])

  const getColumnFixed = (col: ColumnItem, index: number, arr: ColumnItem[]): CSSProperties | undefined => {
    switch (col.fixed) {
      case 'left':
        return {
          left: `${arr.slice(0, index).filter(item => item.fixed).reduce((p, c) => p + (c.width || 0), 0)}px`
        }
      case 'right':
        return {
          right: `${arr.slice(index + 1).filter(item => item.fixed).reduce((p, c) => p + (c.width || 0), 0)}px`
        }
    }
  }

  const { columns, dataSource } = props
  return (
    <table onScroll={handleTableScroll} className='native-table'>
      <thead>
        <tr>
          {
            columns.map((col, ci, arr) => {
              const style: CSSProperties = { width: col.width }
              return (
                <th
                  key={`th-tr-${col.dataIndex}`}
                  className={col.fixed ? `fixed ${col.fixed}` : ''}
                  style={getColumnFixed(col, ci, arr)}
                >
                  <div style={style}>{col.title}</div>
                </th>
              )
            })
          }
        </tr>
      </thead>
      <tbody>
        {
          dataSource.map((data, index) => {
            const rowKey = `tb-tr-${data.key}`
            return (
              <tr key={rowKey}>
                {
                  columns.map((col, ci, arr) => {
                    const style: CSSProperties = { width: col.width }
                    return (
                      <td
                        key={`${rowKey}-${col.dataIndex}`}
                        className={col.fixed ? `fixed ${col.fixed}` : ''}
                        style={getColumnFixed(col, ci, arr)}
                      >
                        <div style={style}>
                          {col.render?.(data[col.dataIndex], index) ?? data[col.dataIndex]}
                        </div>
                      </td>
                    )
                  })
                }
              </tr>
            )
          })
        }
      </tbody>
    </table>
  )
}

export default NativeTable